/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import {
  Animation,
  useClickOutside,
  Typography,
  ZapierLogo,
} from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { useState } from 'react';
import {
  skipLinkHref as skipLinkHrefDefault,
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';
import SkipLink from '../../SkipLink';
import InAppTopbarLink from './InAppTopbarLink';

import AccountActions from '../AccountActions';
import SearchForm from '../SearchForm';
import SearchToggler from './SearchToggler';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';

import type { Props as SearchFormProps } from '../SearchForm';
import type { TopbarProps } from '../types';
import { emitFullStoryEvent } from '../../../utils/fullStoryEvents';

const Styles = labelStyles('UniversalTopbar', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `var(--zds-background-weaker, #FFFDF9)`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: '1px solid var(--zds-stroke-weaker, #D7D3C9)',
    },
  ],
  header: [
    css({
      width: '100%',
      maxWidth: 'none',
      height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
      padding: '0 var(--zds-space-24, 24px) 0 var(--zds-space-8, 8px)',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      alignItems: 'stretch',
      gap: '6px',
      backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',

      '@media (min-width: 450px)': {
        height: UNIVERSAL_TOPBAR_HEIGHT,
        gap: '0',
      },
    }),
  ],
  group: css({
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifySelf: 'flex-end',
    gap: '5px',

    '@media (min-width: 450px)': {
      gap: 'var(--zds-space-8, 8px)',
    },

    '.call-to-action-button a[data-color=tertiary], .call-to-action-button a[data-color=primary]':
      {
        padding: '0 var(--zds-space-12, 12px)',
        height: '36px !important',
        font: `var(--zds-body-2, 500 14px/24px ${Typography.base.fontFamily}) !important`,
        '&:hover': {
          boxShadow: 'none',
        },

        '& > span': {
          padding: 0,
        },

        '&:focus::before': {
          borderColor: 'var(--zds-ui-primary-strongest, #503EBD) !important',
        },
      },
    '.call-to-action-button a[data-color=primary]': {
      backgroundColor: 'var(--zds-ui-primary-stronger, #695BE8)',
      font: `var(--zds-body-3, 600 14px/24px ${Typography.base.fontFamily}) !important`,
      '&:hover, &:focus': {
        backgroundColor: 'var(--zds-ui-primary-strongest, #503EBD)',
      },
      '&:focus-visible::before': {
        border: 'var(--zds-border-focus, 2px solid #695BE8) !important',
      },
    },
    '.call-to-action-button a[data-color=tertiary]': {
      border: '1px solid var(--zds-stroke-weaker, #D7D3C9)',
      color: 'var(--zds-text-default, #413735)',
      backgroundColor: 'var(--zds-background-weaker, #FFFDF9) !important',
      '&:hover, &:focus': {
        color: 'var(--zds-text-default, #413735)',
        border: '1px solid var(--zds-stroke-weaker, #D7D3C9)',
        backgroundColor: 'var(--zds-background-default, #F9F7F3) !important',
      },
      '&:focus-visible::before': {
        border: 'var(--zds-border-focus, 2px solid #695BE8) !important',
      },
    },
  }),
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 'var(--zds-space-12, 12px)',
    },
  ],
  searchForm: (
    props: TopbarProps,
    { isSearching }: { isSearching: boolean }
  ) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        backgroundColor: `var(--zds-background-weaker, #FFFDF9)`,
        padding: '0 10px',
      }),
  ],
  linkButton: css({
    '& span': {
      font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
      fontWeight: 'normal',
      color: 'var(--zds-gray-warm-10, #201515)',
    },
  }),
  searchToggler: ({ isSearching }: { isSearching: boolean }) => [
    {
      font: `var(--zds-body-1, 500 14px/24px ${Typography.base.fontFamily})`,
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

/**
 * @private
 * Renders universal dynamic navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const InAppTopbar = ({
  isSearchFullWidth,
  logoAriaLabel = 'Go to the Zapier Homepage',
  logoHref = '/',
  onLogoClick = () => {},
  selectedHref = '',
  shouldRenderLogo = true,
  size = 'medium',
  skipLinkHref = skipLinkHrefDefault,
  renderAccountMenu = (props: TopbarProps) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications = () => null,
  renderSearchForm = (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  ...otherProps
}: TopbarProps) => {
  const {
    currentUrl,
    getFullyQualifiedURL,
    shouldRenderCallToAction,
    topNavData,
    shouldRenderUpgradeCTA,
  } = useUniversalNavContext();

  const props = {
    isSearchFullWidth,
    logoAriaLabel,
    logoHref,
    onLogoClick,
    renderAccountMenu,
    renderNotifications,
    renderSearchForm,
    selectedHref,
    shouldRenderLogo,
    size,
    skipLinkHref,
    ...otherProps,
  } satisfies TopbarProps;

  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    width: 105,
    height: 28,
  };

  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const onCloseSearch = () => {
    setIsSearching(false);
  };
  const searchForm = props.renderSearchForm({
    isSearching,
    searchInputId,
    onClose: onCloseSearch,
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      if (props.onClickOutside) props.onClickOutside(event);
    },
  });
  const callToActionUrl =
    getFullyQualifiedURL(
      `${topNavData.callToAction?.href}?demo_source=cs_nav_header_${currentUrl}`
    ) || '';

  const upgradeCTAUrl = getFullyQualifiedURL(topNavData.upgradeCTA?.href) || '';

  return (
    <div css={Styles.root} ref={(node) => setClickOutsideNode(node)} data-zds>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.header}
        data-testid="universalTopbar"
        onClick={props.onClick}
        data-zds
      >
        <div css={Styles.group} data-zds>
          {sidebar}
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper}>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref)}
                onClick={props.onLogoClick}
                data-zds
              >
                <ZapierLogo {...logoSize} />
              </a>

              <div css={Styles.skipLink} data-zds>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent} data-zds>
          {
            <div
              aria-label="Search apps"
              css={Styles.searchForm(props, { isSearching })}
              onBlur={() => {
                setIsSearching(false);
              }}
              onFocus={() => {
                setIsSearching(true);
                emitUserInteractionEvent({
                  interaction_name: searchInputId,
                  event_action: 'in_view',
                  option_selected: 'Universal Nav Search opened',
                });
              }}
              role="form"
              data-zds
            >
              {searchForm}
            </div>
          }
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group}
          role="form"
          data-zds
        >
          {!!searchForm && (
            <div
              css={Styles.searchToggler({ isSearching })}
              data-testid="searchToggler"
              data-zds
            >
              <SearchToggler
                id={searchInputId}
                isSearching={false}
                size={props.size}
              />
            </div>
          )}

          {topNavData.linksInApp?.map((link) => (
            <InAppTopbarLink
              navigationItem={{
                variant: 'link',
                ...topNavData.callToAction,
                href: link.href,
                text: size === 'medium' ? link.text : '',
              }}
              position="actions-menu"
              size={props.size}
              isButton={false}
              icon={link.icon}
              ariaLabel={link.text}
              key={link.text}
            >
              {size === 'medium' ? link.text : ''}
            </InAppTopbarLink>
          ))}

          {topNavData.callToAction &&
            props.size !== 'small' &&
            shouldRenderCallToAction && (
              <InAppTopbarLink
                className="call-to-action-button"
                navigationItem={{
                  variant: 'link',
                  color: 'tertiary',
                  ...topNavData.callToAction,
                  href: callToActionUrl,
                }}
                position="actions-menu"
                isButton={true}
                buttonSize="medium"
              >
                {topNavData.callToAction.text}
              </InAppTopbarLink>
            )}

          {topNavData.upgradeCTA &&
            props.size !== 'small' &&
            shouldRenderUpgradeCTA && (
              <InAppTopbarLink
                className="call-to-action-button"
                navigationItem={{
                  variant: 'link',
                  ...topNavData.upgradeCTA,
                  href: upgradeCTAUrl,
                  text: topNavData.upgradeCTA.text,
                  color: topNavData.upgradeCTA.color,
                }}
                buttonSize="medium"
                position="actions-menu"
                isButton={true}
                ariaLabel={topNavData.upgradeCTA.text}
                key={topNavData.upgradeCTA.text}
                onClick={() => {
                  const eventProperties = {
                    interaction_name:
                      'universal_topbar_upgrade_cta_button_clicked',
                    event_action: 'click',
                    option_selected: 'n/a',
                    experiment_flag: true,
                    experiment_name: 'revenue-growth-universal-nav-upgrade-cta',
                    interaction_raw_text: topNavData.upgradeCTA?.text ?? '',
                  };

                  emitUserInteractionEvent(eventProperties);
                  emitFullStoryEvent(eventProperties);
                }}
              >
                {topNavData.upgradeCTA?.text}
              </InAppTopbarLink>
            )}

          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

export default InAppTopbar;
